import React from 'react';
import ChartistGraph from 'react-chartist';
import Chartist from 'chartist';
import { overviewTabs } from '../../../constant';

const seriesNumber = {
  0: 'a',
  1: 'b',
  2: 'c',
  3: 'd',
  4: 'e',
  5: 'f',
};

// the months changed
const isNewMonth = function (date, index, _labels) {
  const dt = new Date(date);

  // handle zero-based day parsing
  dt.setDate(dt.getDate() + 1);

  return dt.getDate() === 1 || index === 0;
};

// should we display the label or point?
const shouldDisplayPoint = (value, index, labels) => {
  if (!value) return false;

  // last item in the series doesn't pass in a labels array
  if (!labels) labels = [];

  // calculate based on width of chart
  const w = document.getElementById('graph').clientWidth,
    itemw = w / labels.length,
    spacer = Math.floor(w / itemw / 10);

  // if our divisor says so, or we have a new month
  return index % spacer === 0 || isNewMonth(value, index, labels);
};

// track if we've switched to a new year
const showDateLabel = (value, index, _labels) => {
  const ts = Date.parse(value),
    dt = new Date(ts);

  // handle zero-based day parsing
  dt.setDate(dt.getDate() + 1);

  // if it's january 1, show the year
  if (dt.getDate() === 1 && dt.getMonth() === 0) {
    return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // if it's a new month, show the month name
  if (dt.getDate() === 1 || index === 0) {
    return dt.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  }

  // otherwise, just return the day
  return dt.getDate().toString();
};

// format numbers according for american financial standards
const commaSeparatedNumber = (val) => {
  while (/(\d+)(\d{3})/.test(val.toString())) {
    val = val.toString().replace(/(\d+)(\d{3})/, '$1' + ',' + '$2');
  }
  return val;
};

// main options for the chart
const graphOptions = {
  showPoint: true,
  showArea: true,
  low: 0,
  fullWidth: true,
  lineSmooth: Chartist.Interpolation.monotoneCubic({
    fillHoles: true,
    tension: 0.1,
  }),
  chartPadding: {
    left: 0,
    top: 10,
    right: 10,
    bottom: 20,
  },
  axisX: {
    showGrid: true,
    showLabel: true,
    labelOffset: {
      x: -5,
      y: 5,
    },
    labelInterpolationFnc: (value, index, labels) => {
      return shouldDisplayPoint(value, index, labels) ? showDateLabel(value, index, labels) : null;
    },
  },
  axisY: {
    showGrid: true,
    showLabel: true,
    onlyInteger: true,
    labelOffset: {
      x: 0,
      y: 8,
    },
    labelInterpolationFnc: (number) => {
      if (number >= 1e15) {
        return (number / 1e15).toFixed(1).replace(/\.0$/, '') + 'Q';
      } else if (number >= 1e12) {
        // If number is in the trillions
        return (number / 1e12).toFixed(1).replace(/\.0$/, '') + 'T';
      } else if (number >= 1e9) {
        // If number is in the billions
        return (number / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
      } else if (number >= 1e6) {
        // If number is in the millions
        return (number / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
      } else if (number >= 1e3) {
        // If number is in the thousands
        return (number / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
      } else {
        return number.toString();
      }
    },
  },
};

// data elements to points
const pointDataElements = (data, _graphdata, pointShowing, newMonth) => {
  // if we don't want this point, remove it
  if (!pointShowing) {
    data.element.remove();
  } else {
    // if we're on a new month, mark with month-start
    let pointclasses = data.element.attr('class') + ' poptip';
    if (newMonth) pointclasses += ' month-start';
    data.element.attr({
      class: pointclasses,
    });
  }
};

// graph draw listener
const onGraphDraw = (data, graphdata) => {
  // determine if we're showing this point
  const pointShowing = shouldDisplayPoint(graphdata.labels[data.index], data.index, graphdata.labels),
    newMonth = isNewMonth(graphdata.labels[data.index], data.index, graphdata.labels);

  // add data elements to points for use in poptips
  if (data.type === 'point') {
    pointDataElements(data, graphdata, pointShowing, newMonth);
  }

  // make our main axes stylable by css
  if (data.type === 'grid' && data.index === 0) {
    data.element.attr({ class: data.element.attr('class') + ' main-axis' });
  }

  // style a month change vertical grid line
  if (data.type === 'grid' && newMonth) {
    data.element.attr({ class: data.element.attr('class') + ' new-month-axis' });
  }

  // style a label for a new month
  if (data.type === 'label' && data.axis.units.dir === 'horizontal' && newMonth) {
    const $span = data.element._node.firstChild.classList;
    $span.add('new-month');
  }
};

// build the graph
const Graph = ({ graphdata, type, className }) => {
  const [hover, setHover] = React.useState(null);
  const graphRef = React.useRef(null);

  React.useEffect(() => {
    const el = graphRef.current;
    if (!el) return;
    const onLeave = () => setHover(null);
    el.addEventListener('mouseleave', onLeave);
    return () => el.removeEventListener('mouseleave', onLeave);
  }, []);

  const handleMouseMove = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const labels = graphdata.labels;
    if (!labels || !labels.length) return;

    const svgEl = el.querySelector('svg');
    if (!svgEl) return;

    const ctm = svgEl.getScreenCTM();
    const reusablePt = svgEl.createSVGPoint();
    const n = labels.length;

    // convert mouse to SVG local space early so we can use it for index calculation too
    const svgMousePt = svgEl.createSVGPoint();
    svgMousePt.x = e.clientX;
    svgMousePt.y = e.clientY;
    const localMouse = svgMousePt.matrixTransform(ctm.inverse());

    // derive i from the actual chart data area in SVG space (accounts for y-axis offset)
    const anyPath = svgEl.querySelector('.ct-line');
    const anyPathLen = anyPath ? anyPath.getTotalLength() : 0;
    const svgDataStartX = anyPath ? anyPath.getPointAtLength(0).x : 0;
    const svgDataEndX = anyPath ? anyPath.getPointAtLength(anyPathLen).x : 1;
    const i = Math.max(
      0,
      Math.min(n - 1, Math.round(((localMouse.x - svgDataStartX) / (svgDataEndX - svgDataStartX)) * (n - 1))),
    );

    // person (user) > group > org (firm) when distances are tied
    const typePriority = { person: 0, group: 1, org: 2 };

    let dotSi = null;
    let dotX = mouseX;
    let dotY = mouseY;
    let dotDist = Infinity;
    let dotPriority = Infinity;
    const allValues = [];

    graphdata.series.forEach((s, si) => {
      const path = el.querySelector(`.ct-series-${seriesNumber[si]} .ct-line`);
      if (!path) return;

      const value = s.data ? s.data[i] : null;
      if (value == null) return;

      // binary search for path Y at mouse X
      let lo = 0,
        hi = path.getTotalLength();
      for (let iter = 0; iter < 25; iter++) {
        const mid = (lo + hi) / 2;
        if (path.getPointAtLength(mid).x < localMouse.x) lo = mid;
        else hi = mid;
      }
      const pathPt = path.getPointAtLength((lo + hi) / 2);
      reusablePt.x = pathPt.x;
      reusablePt.y = pathPt.y;
      const screen = reusablePt.matrixTransform(ctm);
      const relX = screen.x - rect.left;
      const relY = screen.y - rect.top;

      allValues.push({ si, name: s.name, value, type: s.type });

      const yDist = Math.abs(relY - mouseY);
      const priority = typePriority[s.type] !== undefined ? typePriority[s.type] : 3;
      const isClearlyCloser = yDist < dotDist - 10;
      const isTied = yDist <= dotDist + 10 && priority < dotPriority;

      if (isClearlyCloser || isTied) {
        dotSi = si;
        dotX = relX;
        dotY = relY;
        dotDist = yDist;
        dotPriority = priority;
      }
    });

    if (dotSi === null) {
      setHover(null);
      return;
    }

    // sort: Won Revenue first (org → group → member), then Projected (org → group → member)
    const typeDisplayOrder = { org: 0, group: 1, person: 2, person_projected: 5 };
    allValues.sort((a, b) => {
      const aProjected = a.name && a.name.includes('Projected Revenue') ? 3 : 0;
      const bProjected = b.name && b.name.includes('Projected Revenue') ? 3 : 0;
      const aOrder = (typeDisplayOrder[a.type] !== undefined ? typeDisplayOrder[a.type] : 2) + aProjected;
      const bOrder = (typeDisplayOrder[b.type] !== undefined ? typeDisplayOrder[b.type] : 2) + bProjected;
      return aOrder - bOrder;
    });
    setHover({ dotX, dotY, w: rect.width, si: dotSi, values: allValues });
  };

  const formatHoverValue = (val) => {
    if (graphdata.which === overviewTabs[1].which) {
      return '$' + commaSeparatedNumber(val);
    }
    return val;
  };

  const options = graphOptions;
  const event = {
    draw: (data) => onGraphDraw(data, graphdata),
  };
  // if we're showing score, enforce the scale
  if (graphdata.which === overviewTabs[0].which) {
    options.high = 100;
    options.scaleMinSpace = 25;
  } else {
    options.high = null;
    options.scaleMinSpace = null;
  }
  return (
    <>
      <div id="graph" ref={graphRef} onMouseMove={handleMouseMove}>
        <ChartistGraph data={graphdata} type={type} className={className} options={options} listener={event} />
        {hover && (
          <>
            <div
              className={`graph-hover-indicator ct-series-${seriesNumber[hover.si]}`}
              style={{ left: hover.dotX, top: hover.dotY }}
            />
            <div
              className={`graph-hover-tooltip${hover.dotX > hover.w / 2 ? ' graph-hover-tooltip--left' : ''}`}
              style={{ left: hover.dotX, top: hover.dotY }}
            >
              {hover.values.map((v) => (
                <div key={v.si} className="graph-hover-row">
                  <span className={`graph-hover-dot ct-series-${seriesNumber[v.si]}`} />
                  {v.name}: {formatHoverValue(v.value)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <footer>
        <ul className="ct-legend">
          {graphdata.series.map((item, index) => (
            <li className={`ct-series-${seriesNumber[index]}`} key={index}>
              {item.name}
            </li>
          ))}
        </ul>
      </footer>
    </>
  );
};
export default Graph;
